import { Component, ElementRef, Inject, Input, ViewChild } from '@angular/core';
import { OnDynamicData, OnDynamicMount } from 'ngx-dynamic-hooks';
import { BehaviorSubject } from 'rxjs';

import { KHPoint } from '../../../..';
import { IQuestionEventsService, QUESTION_EVENTS_SERVICE_TOKEN } from '../../../../services/IQuestionEvents';
import { applyFrameToElement, ManipFrame } from '../../../../utils/utils';
import { IScorable, ScorableData } from '../kh-manip-scorable/IScorable';

import { DropRegionConfig, ScorableDropRegion } from './draggable-helpers/ScorabelDropRegion';
import { ScorableDragGroup } from './draggable-helpers/ScorableDragGroup';
import { DragRegionConfig, ScorableDragRegion } from './draggable-helpers/ScorableDragRegion';

export const SCORABLE_CLONE_SUFFIX = '__SCORABLE_CLONE__';
@Component({
  selector: 'kh-manipulative-draggable',
  templateUrl: 'kh-manip-scorable-draggable.html',
  standalone: false
})
export class KhManipScorableDraggable implements IScorable, OnDynamicMount {
  @ViewChild('mainElement') public mainElement!: ElementRef<HTMLImageElement>;
  @Input() public src = '';
  @Input() public frame = '0 0 100 100';
  @Input('is-single-mode') public isSingleMode = 'true';
  @Input('reuse-objects') public reuseObjects = 'false';
  @Input() public reportMode = false;
  @Input() public wiggle = false;
  @Input('return-unregistered') public returnUnregistered = 'true';
  @Input() public svgOnly = false;
  @Input() public value = '';
  @Input() public sampleAnswer = '';
  @Input() public key = '';
  @Input() public onChangeCallback: ((data: ScorableData) => void) | undefined;
  public onDoneLoad: (() => void) | undefined;

  private dropRegions: ScorableDropRegion[] = [];
  private dragRegions: ScorableDragRegion[] = [];
  private dragGroup: ScorableDragGroup | null = null;
  private frameData: ManipFrame | null = null;
  private isLoadingSubject = new BehaviorSubject<boolean>(true);
  private isLoading$ = this.isLoadingSubject.asObservable();
  private dragHasBeenSetDuringLoad = false;
  private hasBeenInteractedWith = false; // This is used to control the "wiggle" animation. If the user has figured out how to drag stuff we don't wiggle anymore
  private isAnimating = false;

  public constructor(@Inject(QUESTION_EVENTS_SERVICE_TOKEN) protected readonly questionEventsService: IQuestionEventsService) {
    this.questionEventsService.hideInputOnlyForManip.next(true);
  }

  public onDynamicMount(data: OnDynamicData): void {
    if (this.mainElement && this.mainElement.nativeElement) {
      this.frameData = applyFrameToElement(this.mainElement.nativeElement, this.frame);
    }

    this.parseChildComponents();
    this.setupWiggle();
  }

  public getManipulativeSize(): ManipFrame {
    const defaultFrame = { x: 0, y: 0, width: 100, height: 100 };
    const newFrame = { ...defaultFrame, ...this.frameData };
    const dragFrame = this.dragGroup?.getFrame();

    if (dragFrame) {
      newFrame.height = dragFrame.height + dragFrame.y;
      newFrame.width = Math.max(newFrame.width, dragFrame.width);
    }

    return newFrame;
  }

  public getScorableData(): ScorableData {
    const answerData: { [key: string]: string } = {};
    const stateData: { [key: string]: string } = {};
    const keyCounts: { [key: string]: number } = {};

    for (const dropRegion of this.dropRegions) {
      const dropRegionData = dropRegion.getKeyAnswerStateData();

      stateData[dropRegionData.dropRegionKey] = dropRegionData.stateValue;

      for (const dragRegion of dropRegion.occupiedDragRegions) {
        const key = dragRegion.config.key;
        if (keyCounts[key]) {
          keyCounts[key]++;
        }
        else {
          keyCounts[key] = 1;
        }

        if (keyCounts[key] > 1) {
          const cloneKey = `${key}${SCORABLE_CLONE_SUFFIX}${keyCounts[key].toString()}`;
          stateData[cloneKey] = JSON.stringify(dragRegion.getPosition());
        }
        else {
          stateData[dragRegion.config.key] = JSON.stringify(dragRegion.getPosition());
        }
      }

      const ignoreInScore = (dropRegion.config.ignoreinscore && dropRegion.config.ignoreinscore.toString() === 'true') ? true : false;
      if (!ignoreInScore && dropRegionData.answerValue !== '') {
        answerData[dropRegionData.dropRegionKey] = dropRegionData.answerValue;
      }
    }

    // set state data
    for (const dragRegion of this.dragRegions) {
      const position = dragRegion.getPosition();

      // do not store the root clone element in the state data since it can't actually move
      if (dragRegion.readyClone === null) {
        stateData[dragRegion.config.key] = JSON.stringify(position);
      }
    }

    // strip out empty keys
    const finalKeys = Object.keys(answerData);
    for (const key of finalKeys) {
      if (answerData[key] === '' || answerData[key] === '[]') {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete answerData[key];
      }
    }

    return { answer: answerData, state: stateData };
  }

  public setScorableData(data: ScorableData, isReportMode = false): void {
    // if setScorableData is called before we're done loading we subscribe to the isLoading$ observable and try again when done
    if (this.isLoadingSubject.value) {
      const sub = this.isLoading$.subscribe((value) => {
        if (!value) {
          this.setScorableData(data, isReportMode);
          sub.unsubscribe();
        }
      });

      return;
    }

    // position the drag regions (only student answers will run this really)
    for (const dragRegion of this.dragRegions) {
      if (isReportMode) {
        dragRegion.setCanDrag(false);
      }

      const position = data.state[dragRegion.config.key];

      if (position && position.includes('{') && position.includes('}')) {
        this.dragHasBeenSetDuringLoad = true;
        const pos = JSON.parse(position);
        dragRegion.updateAnchorPosition(pos.x, pos.y, isReportMode);
      }
    }

    // do any cloned objects
    const stateKeys = Object.keys(data.state);
    const cloneKeys = stateKeys.filter((key) => key.includes(SCORABLE_CLONE_SUFFIX));

    for (const cloneKey of cloneKeys) {
      const realKey = cloneKey.split(SCORABLE_CLONE_SUFFIX)[0];
      const pos = JSON.parse(data.state[cloneKey]) as KHPoint;
      const dragRegion = this.dragRegions.find((d) => d.config.key === realKey);

      if (dragRegion) {
        const clone = new ScorableDragRegion(dragRegion.config);
        clone.updateAnchorPosition(pos.x, pos.y, isReportMode);
        if (isReportMode) {
          clone.setCanDrag(false);
        }
      }
    }

    // Let's call this a heuristic - if no data was found during load we're probably trying to render state data that's just an answer
    // this won't have any positional info so we need to infer fake positions from it
    // In this case the keys in the answer state will be the drop regions, not the drag regions
    if (!this.dragHasBeenSetDuringLoad && isReportMode) {
      this.setDataForReportMode(data);
    }
  }

  private setDataForReportMode(data: ScorableData): void {
    // Temporarily clear all drop region callbacks to prevent triggering recalculation during initial positioning
    const originalCallbacks = this.dropRegions.map((dr) => dr.onChangeCallback);
    this.dropRegions.forEach((dr) => dr.onChangeCallback = undefined);

    for (const dropRegion of this.dropRegions) {
      const dropRegionKey = dropRegion.config.key;
      const dragRegionKey = data.state[dropRegionKey];

      // In this case the answers were set as an array of keys for the drag regions.
      // It's unfortunate this is how the data format was structured but since the previous drop system required this
      // functionality we have to support it unless we re-write the entire server answer system top to bottom and migrate everything.
      if (dragRegionKey && dragRegionKey.includes('[') && dragRegionKey.includes(']')) {
        this.layoutMultipleDragRegionsInDropZone(dragRegionKey, dropRegion);
      }
      else {
        this.layoutSingleDragRegionInDropZone(dragRegionKey, dropRegion, dropRegionKey);
      }
    }

    // Restore all callbacks after positioning is complete
    this.dropRegions.forEach((dr, index) => dr.onChangeCallback = originalCallbacks[index]);
  }

  private layoutMultipleDragRegionsInDropZone(dragRegionKey: string, dropRegion: ScorableDropRegion): void {
    const keys = dragRegionKey.replace('[', '').replace(']', '').split(',');
    const dropRect = dropRegion.getRect();
    const dropRegionPos = dropRegion.getTopLeftPosition();

    const foundRegions: { region: ScorableDragRegion; key: string }[] = [];

    for (const key of keys) {
      let dragRegion = this.dragRegions.find((d) => d.config.key === key);

      // if not found, we should try to find based on the answer-value
      if (!dragRegion) {
        dragRegion = this.dragRegions.find((d) => d.getAnswerValue() === key && d.currentRegion === null && !foundRegions.some((r) => r.region === d));
      }

      if (dragRegion) {
        if (this.reuseObjects === 'true') {
          const config = this.getReportModeCloneConfig(dragRegion);
          const clone = new ScorableDragRegion(config);
          foundRegions.push({ region: clone, key });
        }
        else {
          foundRegions.push({ region: dragRegion, key });
        }
      }
    }

    // Layout items in rows, fitting as many as possible per row
    const padding = 10;
    let currentX = padding;
    let currentY = padding;
    let rowHeight = 0;

    foundRegions.forEach(({ region }) => {
      const itemWidth = region.dragElement.clientWidth;
      const itemHeight = region.dragElement.clientHeight;

      // Check if item fits on current row
      if (currentX + itemWidth + padding > dropRect.width && currentX > padding) {
        // Move to next row
        currentX = padding;
        currentY += rowHeight + padding;
        rowHeight = 0;
      }

      // Position the item
      const absolutePos = { x: dropRegionPos.x + currentX, y: dropRegionPos.y + currentY };
      region.updateAnchorPosition(absolutePos.x, absolutePos.y);

      // Snap to nearest valid point if needed
      const nearestPoint = region.nearestSnapPoint(dropRegion);
      region.updateAnchorPosition(nearestPoint.x, nearestPoint.y);
      dropRegion.addOccupiedRegion(region);
      region.currentRegion = dropRegion;

      // Update position for next item
      currentX += itemWidth + padding;
      rowHeight = Math.max(rowHeight, itemHeight);
    });
  }

  private layoutSingleDragRegionInDropZone(dragRegionKey: string, dropRegion: ScorableDropRegion, dropRegionKey: string): void {
    let dragRegion = this.dragRegions.find((d) => d.config.key === dragRegionKey && d.currentRegion === null);

    // if we didn't find a valid region, then search for one based on answer-value
    if (dragRegion === undefined) {
      dragRegion = this.dragRegions.find((d) => d.getAnswerValue() === dragRegionKey && d.currentRegion === null);
    }

    if (dragRegion) {
      let targetDragRegion = dragRegion;
      if (this.reuseObjects === 'true') {
        const config = this.getReportModeCloneConfig(dragRegion);
        targetDragRegion = new ScorableDragRegion(config);
      }

      const pos = dropRegion.getCenterPosition();

      const middleX = pos.x - (targetDragRegion.dragElement.clientWidth / 2);
      const middleY = pos.y - (targetDragRegion.dragElement.clientHeight / 2);

      targetDragRegion.updateAnchorPosition(middleX, middleY);

      // Add to occupied regions to update border
      this.dropRegions.find((d) => d.config.key === dropRegionKey)?.addOccupiedRegion(targetDragRegion);
      targetDragRegion.currentRegion = dropRegion;
    }
  }

  private getReportModeCloneConfig(original: ScorableDragRegion): DragRegionConfig {
    const config = { ...original.config };
    config.visualElement = original.config.visualElement.cloneNode(true) as HTMLImageElement;
    config.makeclones = false;
    return config;
  }

  private parseChildComponents(): void {
    const childContents = this.mainElement.nativeElement.children;
    this.dropRegions = this.makeDropRegions(childContents);
    this.makeDragRegions(childContents, this.dropRegions).then((dragRegions) => {
      this.dragRegions = dragRegions;
      this.isLoadingSubject.next(false);
      if (this.onDoneLoad) {
        this.onDoneLoad();
      }
    });
  }

  private makeDropRegions(childContents: HTMLCollection): ScorableDropRegion[] {
    const dropRegions: ScorableDropRegion[] = [];

    for (let i = 0; i < childContents.length; i++) {
      const child = childContents[i];

      if (child.tagName === 'DROP-REGION') {
        const config = this.processAttributes<DropRegionConfig>(child);
        config.issinglemode = this.isSingleMode !== 'false';
        const dr = new ScorableDropRegion(config);
        dr.onChangeCallback = this.onRegionChanged.bind(this);
        dropRegions.push(dr);
      }
    }

    return dropRegions;
  }

  private async makeDragRegions(childContents: HTMLCollection, dropRegions: ScorableDropRegion[]): Promise<ScorableDragRegion[]> {
    let dragRegions: ScorableDragRegion[] = [];
    const imageLoadPromises: Promise<void>[] = [];

    for (let i = 0; i < childContents.length; i++) {
      const child = childContents[i];

      if (child.tagName === 'DRAG-OBJECT') {
        const config = this.processAttributes<DragRegionConfig>(child);
        config.returnunregistered = this.returnUnregistered === 'true';
        config.dropRegions = dropRegions;
        config.makeclones = this.reuseObjects === 'true';
        config.createIndex = i;

        if ((config as any).src) {
          const img = new Image();
          img.src = (config as any).src;
          imageLoadPromises.push(new Promise((resolve) => {
            img.onload = () => {
              config.visualElement = img;

              dragRegions.push(new ScorableDragRegion(config));
              resolve();
            };
          }));
        }
      }
    }

    await Promise.all(imageLoadPromises);

    // ensure the regions are rendered in the order of the markup. The async image load messes up the order.
    dragRegions = dragRegions.sort((a, b) => a.config.createIndex - b.config.createIndex);

    this.dragGroup = new ScorableDragGroup(this.mainElement.nativeElement,
      {
        x: (this.frameData?.width || 0) / 2,
        y: this.frameData?.height || 0
      }, this.frameData?.width || 100, dragRegions);

    return dragRegions;
  }

  private onRegionChanged(): void {
    if (this.onChangeCallback) {
      this.onChangeCallback(this.getScorableData());
    }

    this.hasBeenInteractedWith = true;

    this.dragGroup?.recalculateAndAnimate();
  }

  private static readonly STRING_ONLY_ATTRIBUTES = new Set(['key']);

  private processAttributes<T>(child: Element): T {
    const attributes = child.attributes;
    const config: any = {
      parent: this.mainElement.nativeElement
    };

    for (let i = 0; i < attributes.length; i++) {
      const attribute = attributes[i];
      const name = attribute.name.toLowerCase().replace(/-/g, ''); // replace all hyphens
      const lowerValue = attribute.value.toLowerCase();
      if (!KhManipScorableDraggable.STRING_ONLY_ATTRIBUTES.has(name) && (lowerValue === 'true' || lowerValue === 'false')) { // convert stringified bools
        config[name] = lowerValue === 'true';
      }
      else {
        config[name] = attribute.value;
      }
    }

    return config as T;
  }

  private setupWiggle(): void {
    if (!this.reportMode && this.wiggle) {
      this.mainElement.nativeElement.addEventListener('click', this.wiggleAnimation.bind(this));
    }
  }

  private wiggleAnimation(e: MouseEvent): void {
    if (this.hasBeenInteractedWith || this.isAnimating) {
      return;
    }

    if (this.dragRegions.length === 0) {
      return;
    }

    this.isAnimating = true;

    const timelines: gsap.core.Timeline[] = [];
    let completedTimelines = 0;
    let animationFinished = false;

    const finishAnimation = () => {
      if (animationFinished) {
        return;
      }

      for (const dragRegion of this.dragRegions) {
        if (dragRegion.readyClone) {
          dragRegion.readyClone.dragElement.removeEventListener('mousedown', killTimelines);
        }
        else {
          dragRegion.dragElement.removeEventListener('mousedown', killTimelines);
        }
      }

      animationFinished = true;
      this.isAnimating = false;
    };

    const killTimelines = () => {
      for (let i = 0; i < timelines.length; i++) {
        timelines[i].progress(1).kill();
      }

      finishAnimation();
    };

    for (const dragRegion of this.dragRegions) {
      const direction = Math.random() > 0.5 ? 1 : -1;
      const timeline = dragRegion.wiggleAnimation(direction);

      timeline.eventCallback('onComplete', () => {
        completedTimelines++;

        if (completedTimelines === this.dragRegions.length) {
          finishAnimation();
        }
      });

      timelines.push(timeline);

      if (dragRegion.readyClone) {
        dragRegion.readyClone.dragElement.addEventListener('mousedown', killTimelines);
      }
      else {
        dragRegion.dragElement.addEventListener('mousedown', killTimelines);
      }
    }
  }
}
